import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const DEFAULT_SENSITIVITY = 0.002;
const ZOOM_SPEED = 1;

const FirstPersonDragControls = forwardRef(function FirstPersonDragControls(
  _,
  ref
) {
  const { camera, gl } = useThree();
  const isDragging = useRef<boolean>(false);
  const prev = useRef<[number, number] | null>(null);
  const yaw = useRef(0); // left/right
  const pitch = useRef(-Math.PI * 0.33);
  const isTransformDragging = useRef<boolean>(false);

  const checkTransformControlsActive = () => {
    return (window as any).isTransformControlsDragging === true;
  };

  const onMouseDown = (e: MouseEvent) => {
    (window as any).mouseX = e.clientX;
    (window as any).mouseY = e.clientY;

    if (checkTransformControlsActive()) {
      isTransformDragging.current = true;
      return;
    }

    isDragging.current = true;
    prev.current = [e.clientX, e.clientY];
  };

  const onMouseMove = (e: MouseEvent) => {
    (window as any).mouseX = e.clientX;
    (window as any).mouseY = e.clientY;

    if (isTransformDragging.current || checkTransformControlsActive()) {
      return;
    }

    if (!isDragging.current || !prev.current) return;

    const [prevX, prevY] = prev.current;
    const deltaX = e.clientX - prevX;
    const deltaY = e.clientY - prevY;

    yaw.current -= deltaX * DEFAULT_SENSITIVITY;
    pitch.current -= deltaY * DEFAULT_SENSITIVITY;

    // Clamp vertical look to avoid flipping
    pitch.current = Math.max(
      -Math.PI / 2 + 0.01,
      Math.min(Math.PI / 2 - 0.01, pitch.current)
    );

    prev.current = [e.clientX, e.clientY];
  };

  const onMouseUp = () => {
    isDragging.current = false;
    isTransformDragging.current = false;
    prev.current = null;
  };

  const onWheel = (e: WheelEvent) => {
    if (isTransformDragging.current || checkTransformControlsActive()) {
      return;
    }

    const dir = new THREE.Vector3();
    camera.getWorldDirection(dir);
    dir.multiplyScalar(e.deltaY > 0 ? ZOOM_SPEED : -ZOOM_SPEED);
    camera.position.add(dir);
  };

  useFrame(() => {
    if (isTransformDragging.current || checkTransformControlsActive()) {
      return;
    }

    const quat = new THREE.Quaternion();
    quat.setFromEuler(new THREE.Euler(pitch.current, yaw.current, 0, 'YXZ'));
    camera.quaternion.copy(quat);
  });

  useEffect(() => {
    const el = gl.domElement;
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mouseleave', onMouseUp);
    el.addEventListener('wheel', onWheel);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        (window as any).isTransformControlsDragging = false;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mouseleave', onMouseUp);
      el.removeEventListener('wheel', onWheel);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [camera, gl]);

  useImperativeHandle(ref, () => ({
    lookAtObject: (obj: THREE.Object3D) => {
      const targetPos = new THREE.Vector3();
      obj.getWorldPosition(targetPos);

      const dir = new THREE.Vector3()
        .subVectors(targetPos, camera.position)
        .normalize();

      let newYaw = Math.atan2(-dir.x, -dir.z);
      let newPitch = Math.asin(dir.y);
      newPitch = THREE.MathUtils.clamp(
        newPitch,
        -Math.PI / 2 + 0.01,
        Math.PI / 2 - 0.01
      );

      yaw.current = newYaw;
      pitch.current = newPitch;
    },
  }));

  return null;
});

export default FirstPersonDragControls;
